import {
  Alert, Button, Card, Descriptions, Empty, Input, Modal, Space, Spin, Tag, Typography,
} from 'antd';
import rideDemandApi from 'api/rideDemandApi';
import rideGeocodingApi from 'api/rideGeocodingApi';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  formatDemandDate, formatDemandMoney, getRideDemandErrorMessage, RideDemandStatusTag,
} from '../../rideDemandsConstants';

const { Text, Title } = Typography;

const PAYMENT_METHOD_LABELS = { CASH: 'Tiền mặt', WALLET: 'Ví EVG' };

// The address text stored on the demand can be a generic fallback (e.g.
// "Khu vực hiện tại") when the app's free reverse-geocoding provider failed
// at submission time — the GPS coordinates underneath are still exact and
// always captured, so this re-resolves the real place name from them on the
// admin's behalf, independent of whatever the app managed to save.
function RidePointDisplay({ point }) {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasCoords = point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude);

  useEffect(() => {
    setResolved(null);
    if (!hasCoords) return;
    let cancelled = false;
    setLoading(true);
    rideGeocodingApi.reverse(point.latitude, point.longitude)
      .then((response) => { if (!cancelled) setResolved(response?.address || null); })
      .catch(() => { if (!cancelled) setResolved(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hasCoords, point?.latitude, point?.longitude]);

  if (!point) return '—';

  return (
    <span>
      {resolved || point.address || '—'}
      {loading && <Text type="secondary"> (đang tra cứu tên địa điểm…)</Text>}
      {hasCoords && (
        <>
          {' '}
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${point.latitude},${point.longitude}`}
            target="_blank"
            rel="noreferrer"
          >
            (Xem trên bản đồ)
          </a>
        </>
      )}
    </span>
  );
}

function RideDemandsDetailScreen() {
  const router = useRouter();
  const demandId = router.query?.demandId;
  const { user, permissionList } = useSelector((state) => state.auth);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';
  const canManage = isAdmin || permissionList?.some((permission) => permission?.code === 'RIDE_DEMAND_MANAGE');

  const loadDetail = useCallback(async () => {
    if (!demandId) return;
    setDetailLoading(true);
    try {
      const response = await rideDemandApi.getDemand(demandId);
      setDetail(response?.demand || null);
    } catch (error) {
      setDetail(null);
      ToastHelper.showError(getRideDemandErrorMessage(error));
    } finally {
      setDetailLoading(false);
    }
  }, [demandId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const openCancelModal = () => {
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) return;
    setCancelLoading(true);
    try {
      await rideDemandApi.cancelDemand(demandId, cancelReason.trim());
      ToastHelper.showSuccess('Đã hủy yêu cầu hẹn giờ');
      setCancelModalOpen(false);
      await loadDetail();
    } catch (error) {
      ToastHelper.showError(getRideDemandErrorMessage(error));
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div>
      <div>
        {UIHelper.buildBreadcrumb([
          {
            title: 'Yêu cầu hẹn giờ xe ghép',
            onPress: () => router.navigate(-1),
          },
          { title: detail ? `Yêu cầu ${String(detail.demandId).slice(0, 8)}` : 'Chi tiết yêu cầu' },
        ])}
      </div>
      <div className="container-fluid px-4 my-4">
        <Card>
          <Spin spinning={detailLoading}>
            {!detail && !detailLoading ? <Empty description="Không tải được yêu cầu" /> : detail && (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <Title level={4} className="mb-0">
                    Yêu cầu
                    {' '}
                    {String(detail.demandId).slice(0, 8)}
                  </Title>
                  <Space>
                    <RideDemandStatusTag status={detail.status} />
                    {canManage && detail.status === 'OPEN' && (
                      <Button danger onClick={openCancelModal}>Hủy yêu cầu</Button>
                    )}
                  </Space>
                </div>

                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Khách hàng">
                    {detail.customer?.fullname || `User #${detail.customer?.userId || ''}`}
                    {' · '}
                    {detail.customer?.phone || detail.customer?.email || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Điểm đón"><RidePointDisplay point={detail.pickup} /></Descriptions.Item>
                  <Descriptions.Item label="Điểm trả"><RidePointDisplay point={detail.dropoff} /></Descriptions.Item>
                  <Descriptions.Item label="Giờ đón">
                    {formatDemandDate(detail.departureFrom)}
                  </Descriptions.Item>
                  <Descriptions.Item label={detail.isCharter ? 'Loại xe cần bao' : 'Số ghế cần đi'}>
                    {detail.isCharter ? `Xe ${detail.charterVehicleCapacity || 4} chỗ` : `${detail.seats} ghế`}
                    {detail.isCharter && <Tag color="gold" style={{ marginLeft: 8 }}>Bao xe</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ghi chú">{detail.note || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Phương thức thanh toán">
                    {PAYMENT_METHOD_LABELS[detail.paymentMethod] || detail.paymentMethod}
                  </Descriptions.Item>
                  <Descriptions.Item label="Giá mỗi ghế (dự kiến)">{formatDemandMoney(detail.fare?.farePerSeat)}</Descriptions.Item>
                  <Descriptions.Item label="Tổng tiền trước giảm giá">{formatDemandMoney(detail.fare?.totalFare)}</Descriptions.Item>
                  {/* fare.totalFare from the API is always the PRE-discount
                      amount (same convention as RideBooking) — the promo
                      discount is tracked separately in promoCode/
                      promoDiscountAmount and was previously never surfaced
                      here at all, so admin had no way to see a promo was
                      applied or what the customer actually owed. */}
                  {detail.fare?.promoCode && (
                    <Descriptions.Item label="Mã khuyến mãi đã áp dụng">
                      <Tag color="green">{detail.fare.promoCode}</Tag>
                      {' -'}
                      {formatDemandMoney(detail.fare.promoDiscountAmount)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Tổng tiền khách phải trả">
                    <Text strong style={{ color: '#389e0d', fontSize: 16 }}>
                      {formatDemandMoney(
                        (detail.fare?.totalFare || 0) - (detail.fare?.promoDiscountAmount || 0),
                      )}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Chuyến đã nhận">
                    {detail.matchedTripCode ? detail.matchedTripCode : 'Chưa có tài xế nhận'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Tạo lúc">{formatDemandDate(detail.createdAt)}</Descriptions.Item>
                </Descriptions>

                {detail.status === 'CANCELLED' && (
                  <Card size="small" title="Lý do hủy">
                    <Text>{detail.cancellationReason || 'Không có lý do'}</Text>
                  </Card>
                )}
              </Space>
            )}
          </Spin>
        </Card>
      </div>

      <Modal
        title="Hủy yêu cầu hẹn giờ"
        open={cancelModalOpen}
        okText="Xác nhận hủy"
        cancelText="Đóng"
        okButtonProps={{ danger: true, disabled: !cancelReason.trim() }}
        confirmLoading={cancelLoading}
        onCancel={() => setCancelModalOpen(false)}
        onOk={submitCancel}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Hành động này không thể hoàn tác"
            description="Yêu cầu đang mở này sẽ chuyển sang trạng thái Đã hủy và không còn hiện cho tài xế nào nữa. Khách hàng sẽ nhận được thông báo kèm lý do."
          />
          <Input.TextArea
            rows={4}
            maxLength={500}
            showCount
            value={cancelReason}
            placeholder="Nhập lý do hủy để khách hàng biết vì sao"
            onChange={(event) => setCancelReason(event.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
}

export default RideDemandsDetailScreen;
